// external
import { useContext, useCallback } from "react";
import { toast } from "react-toastify";

// state managing/security
import { FaoResultsContext } from "context/FaoResultsContext";
import { FaoBuilderContext } from "context/FaoBuilderContext";

// api
import apiRoot from "api/apiRoot";


function useAdmitAdjustmentHandlers() {
  const { modelConfig, setModelConfig } = useContext(FaoBuilderContext);
  const {
    admitAdjustmentUserInput,
    setAdmitAdjustmentUserInput,
    setIsSaving,
    setIsSaved,
    admitAdjustmentData,
  } = useContext(FaoResultsContext);


  const validatePercentageInput = useCallback((value) => {
    if (value === '') return '';
    
    // Check for minus sign only at the beginning
    if (value.includes('-') && value.indexOf('-') !== 0) return '';
    
    // Remove minus sign for further validation
    const valueWithoutMinus = value.startsWith('-') ? value.slice(1) : value;
    
    // Check for multiple decimal points
    const decimalCount = (valueWithoutMinus.match(/\./g) || []).length;
    if (decimalCount > 1) return '';
    
    // Use a more specific, non-backtracking pattern
    // This pattern explicitly defines the structure: optional digits, optional decimal, optional digits
    const validPattern = /^\d*\.?\d*$/;
    if (!validPattern.test(valueWithoutMinus)) return '';
    
    return value;
  }, []);


  const handlePercentageChange = useCallback((regime, needRankMin, academicRankIndex, field, value) => {
    const validatedValue = validatePercentageInput(value);
    
    setAdmitAdjustmentUserInput(prev => {
      const newInput = { ...prev };
      if (!newInput[regime]) newInput[regime] = { academicDataByNeedRank: {} };
      if (!newInput[regime].academicDataByNeedRank[needRankMin]) newInput[regime].academicDataByNeedRank[needRankMin] = [];
      while (newInput[regime].academicDataByNeedRank[needRankMin].length <= academicRankIndex) {
        newInput[regime].academicDataByNeedRank[needRankMin].push({ increase: '0', yield: '0' });
      }
      newInput[regime].academicDataByNeedRank[needRankMin][academicRankIndex][field] = validatedValue;
      return newInput;
    });
  }, [validatePercentageInput, setAdmitAdjustmentUserInput]);


  const handlePercentageBlur = useCallback((regime, needRankMin, academicRankIndex, field) => {
    setAdmitAdjustmentUserInput(prev => {
      const newInput = { ...prev };
      const currentValue = newInput[regime]?.academicDataByNeedRank?.[needRankMin]?.[academicRankIndex]?.[field];
      
      if (currentValue === '') {
        if (!newInput[regime]) newInput[regime] = { academicDataByNeedRank: {} };
        if (!newInput[regime].academicDataByNeedRank[needRankMin]) newInput[regime].academicDataByNeedRank[needRankMin] = [];
        while (newInput[regime].academicDataByNeedRank[needRankMin].length <= academicRankIndex) {
          newInput[regime].academicDataByNeedRank[needRankMin].push({ increase: '0', yield: '0' });
        }
        newInput[regime].academicDataByNeedRank[needRankMin][academicRankIndex][field] = '0';
      }
      
      return newInput;
    });
  }, [setAdmitAdjustmentUserInput]);


  const handleAdmitAdjustmentSave = useCallback(async () => {
    if (!modelConfig?.id) {
      toast.error("Model ID is required to save admit adjustment data");
      return;
    }

    setIsSaving((prev) => ({ ...prev, admitAdjustments: true }));
    setIsSaved((prev) => ({ ...prev, admitAdjustments: false }));

    try {
      const toastId = toast.loading("Saving Admit Adjustments...");
      

      const completeData = {};
      
      Object.entries(admitAdjustmentData).forEach(([regime, regimeData]) => {
        completeData[regime] = {
          needRanks: regimeData.needRanks,
          academicDataByNeedRank: {}
        };
        
        Object.entries(regimeData.academicDataByNeedRank).forEach(([needRankMin, academicData]) => {
          completeData[regime].academicDataByNeedRank[needRankMin] = academicData.map((item, index) => {
            const userInput = admitAdjustmentUserInput[regime]?.academicDataByNeedRank?.[needRankMin]?.[index] || {};
            return {
              ...item,
              increase: userInput.increase || item.increase,
              yield: userInput.yield || item.yield
            };
          });
        });
      });
      
      const payload = {
        modelId: modelConfig.id,
        admitAdjustmentData: completeData
      };

      const response = await apiRoot.post("/api/fao-admit-adjustment/save", payload);
      

      toast.update(toastId, {
        render: "Admit Adjustments Saved - Recalculating Aid Results...",
        type: "info",
        isLoading: true,
        autoClose: false,
      });

      try {

        const aidResultsResponse = await apiRoot.post("/api/fao-model/aid-results", {}, {
          params: { modelId: modelConfig.id }
        });
        

        setModelConfig({
          ...modelConfig,
          aidResults: aidResultsResponse.data.aidResults
        });
        
        toast.update(toastId, {
          render: "SUCCESS - Admit Adjustments Saved & Aid Results Updated",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      } catch (aidResultsError) {
        console.error("Error updating aid results:", aidResultsError);
        toast.update(toastId, {
          render: "Admit Adjustments Saved - Aid Results Update Failed",
          type: "warning",
          isLoading: false,
          autoClose: 5000,
        });
      }
      
      setIsSaved((prev) => ({ ...prev, admitAdjustments: true }));
      return response.data;
    } catch (error) {
      console.error("Error saving admit adjustment data:", error);
      toast.error("Failed to save admit adjustments. Please try again.");
      throw error;
    } finally {
      setIsSaving((prev) => ({ ...prev, admitAdjustments: false }));
    }
  }, [modelConfig, setModelConfig, admitAdjustmentUserInput, admitAdjustmentData, setIsSaving, setIsSaved]);


  return {
    handlePercentageChange,
    handlePercentageBlur,
    handleAdmitAdjustmentSave,
  };
}

export default useAdmitAdjustmentHandlers; 